/** S012 chat-list command scheduler and React composition hook. */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  runChatListEffect,
  type ChatListEffect,
  type ChatListEffectContext,
} from './effects';
import type { ChatListMsg } from './messages';
import { initialChatListModel, type ChatListModel } from './model';
import type { ChatListPort } from './port';
import { updateChatList } from './update';

export interface UseChatListProgramProps {
  readonly port: ChatListPort;
}

export interface UseChatListProgramResult {
  readonly model: ChatListModel;
  readonly start: (loadId: number) => void;
}

interface PendingEffect {
  readonly id: number;
  readonly effect: ChatListEffect;
}

interface TrackedEffect {
  readonly controller: AbortController;
  readonly promise: Promise<void>;
}

interface ProgramState {
  readonly model: ChatListModel;
  readonly pendingEffects: readonly PendingEffect[];
  readonly nextEffectId: number;
}

const initialProgramState: ProgramState = Object.freeze({
  model: initialChatListModel,
  pendingEffects: Object.freeze([]),
  nextEffectId: 1,
});

type ProgramAction =
  | { readonly type: 'DomainMessage'; readonly message: ChatListMsg }
  | { readonly type: 'EffectFinished'; readonly id: number };

function scheduleEffects(
  effects: readonly ChatListEffect[],
  nextEffectId: number,
): Pick<ProgramState, 'pendingEffects' | 'nextEffectId'> {
  const pendingEffects = effects.map((effect, index) => ({
    id: nextEffectId + index,
    effect,
  }));
  return {
    pendingEffects,
    nextEffectId: nextEffectId + effects.length,
  };
}

function programReducer(
  state: ProgramState,
  action: ProgramAction,
): ProgramState {
  if (action.type === 'EffectFinished') {
    return {
      ...state,
      pendingEffects: state.pendingEffects.filter(({ id }) => id !== action.id),
    };
  }

  const result = updateChatList(state.model, action.message);
  // Every S012 domain completion emits no follow-up effect. LoadStarted replaces
  // the queue so an obsolete generation is cancelled rather than run in parallel.
  if (action.message.type === 'LoadStarted') {
    const scheduled = scheduleEffects(result.effects, state.nextEffectId);
    return {
      model: result.model,
      pendingEffects: scheduled.pendingEffects,
      nextEffectId: scheduled.nextEffectId,
    };
  }
  return { ...state, model: result.model };
}

export function useChatListProgram({
  port,
}: UseChatListProgramProps): UseChatListProgramResult {
  const [state, dispatch] = useReducer(programReducer, initialProgramState);
  const activeEffects = useRef<Map<number, TrackedEffect>>(new Map());

  const dispatchDomain = useCallback((message: ChatListMsg) => {
    dispatch({ type: 'DomainMessage', message });
  }, []);

  useEffect(() => {
    const pendingIds = new Set(state.pendingEffects.map(({ id }) => id));

    for (const [id, tracked] of activeEffects.current) {
      if (!pendingIds.has(id)) {
        tracked.controller.abort();
        activeEffects.current.delete(id);
      }
    }

    for (const pending of state.pendingEffects) {
      if (activeEffects.current.has(pending.id)) continue;

      const controller = new AbortController();
      const context: ChatListEffectContext = { dispatch: dispatchDomain };
      const promise = runChatListEffect(
        pending.effect,
        context,
        port,
        controller.signal,
      );
      activeEffects.current.set(pending.id, { controller, promise });

      void promise.finally(() => {
        if (activeEffects.current.get(pending.id)?.controller === controller) {
          activeEffects.current.delete(pending.id);
        }
        dispatch({ type: 'EffectFinished', id: pending.id });
      });
    }
  }, [dispatchDomain, port, state.pendingEffects]);

  useEffect(() => () => {
    for (const tracked of activeEffects.current.values()) {
      tracked.controller.abort();
    }
    activeEffects.current.clear();
  }, []);

  const start = useCallback((loadId: number) => {
    dispatchDomain({ type: 'LoadStarted', loadId });
  }, [dispatchDomain]);

  return { model: state.model, start };
}
